/* eslint-disable array-callback-return */
/* eslint-disable consistent-return */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { FormHandles } from '@unform/core';
import { Link } from 'react-router-dom';

import Toolbar from '../../components/Toolbar';
// Replaced MonthPicker with a local MonthStepper for month navigation
import LoadingAnimation from '../../components/Loading';

import api from '../../services/api';
import formatValue from '../../utils/formatValue';

import {
  Container,
  Form,
  Content,
  SummaryGrid,
  StatCard,
  StatLabel,
  StatValue,
  Sections,
  Section,
  SectionTitle,
  CardsGrid,
  Card,
  CardHeader,
  CardTitle,
  ProgressBar,
  ProgressTrack,
  ProgressFill,
  CardMeta,
  MetaItem,
  MetaLabel,
  MetaValue,
  EmptyState,
} from './styles';
import MonthStepper from './MonthStepper';

interface SummaryData {
  income: string;
  outcome: string;
  balance: string;
}

interface AccountState {
  account_id: string;
  name: string;
  initial_balance: number;
  amount_spent: number;
  final_balance: number;
  type: string;
  has_account_weight: boolean;
  account_type: string;
}

interface SavingsAccountState {
  account_id: string;
  name: string;
  description: string;
  amount_saved: number;
  goal: number;
  deleted_at?: string;
}

interface StatesResponse {
  accounts: AccountState[];
  savings_accounts: SavingsAccountState[];
}

interface FormattedAccountState {
  account_id: string;
  name: string;
  initial_balance: number;
  amount_spent: number;
  final_balance: number;
  formatted_initial_balance: string;
  formatted_amount_spent: string;
  formatted_final_balance: string;
  type: string;
  hasWeight?: boolean;
}

interface FormattedSavingsAccountState {
  account_id: string;
  name: string;
  description: string;
  amount_saved: number;
  goal: number;
  formatted_amount_saved: string;
  formatted_goal: string;
}

const AccountsSummaryV2: React.FC = () => {
  const [accountState, setAccountState] = useState<FormattedAccountState[]>([]);
  const [savingsAccountState, setSavingsAccountState] = useState<
    FormattedSavingsAccountState[]
  >([]);
  const [summary, setSummary] = useState<SummaryData>({} as SummaryData);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(new Date());

  const dateFormRef = useRef<FormHandles>(null);

  const handleDateChange = useCallback((compDate) => {
    const now = new Date();
    const sameMonth =
      now.getFullYear() === compDate.getFullYear() &&
      now.getMonth() === compDate.getMonth();
    setDate(sameMonth ? new Date() : compDate);
  }, []);

  // Fetch states based on month
  useEffect(() => {
    const formattedDate = new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      year: 'numeric',
    })
      .format(date)
      .replace('/', '-');

    api
      .get<StatesResponse>('states', {
        params: { date: formattedDate },
      })
      .then((response) => {
        const accStates = response.data.accounts
          .map((state) => {
            if (state.account_id) {
              return {
                account_id: state.account_id,
                name: state.name,
                initial_balance: state.initial_balance,
                amount_spent: state.amount_spent,
                final_balance: state.final_balance,
                formatted_initial_balance: formatValue(
                  state.initial_balance / 100,
                ),
                formatted_amount_spent: formatValue(state.amount_spent / 100),
                formatted_final_balance: formatValue(state.final_balance / 100),
                hasWeight: !!state.has_account_weight,
                type: state.account_type,
              } as FormattedAccountState;
            }

            return {
              account_id: state.account_id,
              name: state.name,
              initial_balance: 0,
              amount_spent: 0,
              final_balance: 0,
              formatted_initial_balance: 'R$ --',
              formatted_amount_spent: 'R$ --',
              formatted_final_balance: 'R$ --',
              hasWeight: true,
              type: 'OUT',
            } as FormattedAccountState;
          })
          .sort((a, b) => a.name.localeCompare(b.name));

        setAccountState(accStates);

        const formattedSavingsStates = response.data.savings_accounts
          .filter((acc) => {
            if (!acc.deleted_at) return true;
            const [month, year] = acc.deleted_at.split('-');
            const [currMonth, currYear] = formattedDate.split('-');
            return month >= currMonth && year >= currYear;
          })
          .map((acc) => ({
            account_id: acc.account_id,
            name: acc.name,
            description: acc.description,
            amount_saved: acc.amount_saved,
            goal: acc.goal,
            formatted_amount_saved: formatValue(acc.amount_saved / 100),
            formatted_goal: formatValue(acc.goal / 100),
          }));

        setSavingsAccountState(formattedSavingsStates);
      })
      .finally(() => setLoading(false));
  }, [date]);

  // Compute summary
  useEffect(() => {
    const totalIncome = accountState.reduce((total, state) => {
      return total + Number(state.initial_balance);
    }, 0);

    const totalOutcome = accountState.reduce((total, state) => {
      return total + Number(state.amount_spent);
    }, 0);

    let currentBalance = +(totalIncome - totalOutcome).toFixed(2);
    if (currentBalance > -0.01 && currentBalance < 0.01) currentBalance = 0;

    setSummary({
      income: formatValue(totalIncome / 100),
      outcome: formatValue(totalOutcome / 100),
      balance: formatValue(currentBalance / 100),
    });
  }, [accountState]);

  const spendingAccounts = useMemo(
    () => accountState.filter((a) => (a.type || 'OUT') === 'OUT'),
    [accountState],
  );

  if (loading) {
    return (
      <Container>
        <Toolbar />
        <LoadingAnimation />
      </Container>
    );
  }

  return (
    <Container>
      <Toolbar />
      <Content>
        <Form ref={dateFormRef} onSubmit={() => {}}>
          <MonthStepper
            date={date}
            onPrev={() => {
              const d = new Date(date);
              d.setMonth(d.getMonth() - 1);
              d.setDate(1);
              setDate(d);
            }}
            onNext={() => {
              const d = new Date(date);
              d.setMonth(d.getMonth() + 1);
              d.setDate(1);
              setDate(d);
            }}
          />
        </Form>

        {/* Summary cards */}
        <SummaryGrid>
          <StatCard>
            <StatLabel>Entradas</StatLabel>
            <StatValue color="green">{summary.income}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Saídas</StatLabel>
            <StatValue color="amber">{summary.outcome}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Saldo Final</StatLabel>
            <StatValue color={summary.balance?.includes('-') ? 'red' : 'green'}>
              {summary.balance}
            </StatValue>
          </StatCard>
        </SummaryGrid>

        <Sections>
          <Section>
            <SectionTitle>Gastos</SectionTitle>
            {spendingAccounts.length ? (
              <CardsGrid>
                {spendingAccounts.map((acc) => {
                  const spent = Math.abs(acc.amount_spent);
                  const hasBudget = acc.initial_balance > 0;
                  const pct = hasBudget
                    ? Math.min(100, Math.max(0, (spent / acc.initial_balance) * 100))
                    : spent > 0
                    ? 100
                    : 0;
                  const over = acc.final_balance < 0 || spent > acc.initial_balance;

                  return (
                    <Card key={acc.account_id}>
                      <CardHeader>
                        <CardTitle>{acc.name}</CardTitle>
                      </CardHeader>
                      <ProgressBar>
                        <ProgressTrack>
                          <ProgressFill
                            color={over ? 'red' : 'amber'}
                            style={{ width: `${pct}%` }}
                          />
                        </ProgressTrack>
                      </ProgressBar>
                      <CardMeta>
                        <MetaItem>
                          <MetaLabel>Saldo inicial</MetaLabel>
                          <MetaValue>{acc.formatted_initial_balance}</MetaValue>
                        </MetaItem>
                        <MetaItem>
                          <MetaLabel>Gasto</MetaLabel>
                          <MetaValue>{acc.formatted_amount_spent}</MetaValue>
                        </MetaItem>
                        <MetaItem>
                          <MetaLabel>Saldo Final</MetaLabel>
                          <MetaValue>{acc.formatted_final_balance}</MetaValue>
                        </MetaItem>
                      </CardMeta>
                    </Card>
                  );
                })}
              </CardsGrid>
            ) : (
              <EmptyState>
                Parece que você não tem gastos registrados ainda.
{' '}
                <Link to="account-register">Crie uma conta aqui</Link>
{' '}
ou
<span> </span>
                <Link to="transactions">adicione uma transação</Link>.
              </EmptyState>
            )}
          </Section>

          <Section>
            <SectionTitle>Poupanças</SectionTitle>
            {savingsAccountState.length ? (
              <CardsGrid>
                {savingsAccountState.map((sa) => {
                  const pct = sa.goal
                    ? Math.min(
                        100,
                        Math.max(
                          0,
                          (sa.amount_saved / Math.max(1, sa.goal)) * 100,
                        ),
                      )
                    : 0;
                  const color = sa.amount_saved >= sa.goal ? 'green' : 'amber';
                  return (
                    <Card key={sa.account_id}>
                      <CardHeader>
                        <CardTitle>{sa.name}</CardTitle>
                      </CardHeader>
                      <ProgressBar>
                        <ProgressTrack>
                          <ProgressFill
                            color={color}
                            style={{ width: `${pct}%` }}
                          />
                        </ProgressTrack>
                      </ProgressBar>
                      <CardMeta>
                        <MetaItem>
                          <MetaLabel>Poupado</MetaLabel>
                          <MetaValue>{sa.formatted_amount_saved}</MetaValue>
                        </MetaItem>
                        <MetaItem>
                          <MetaLabel>Meta</MetaLabel>
                          <MetaValue>{sa.formatted_goal}</MetaValue>
                        </MetaItem>
                      </CardMeta>
                    </Card>
                  );
                })}
              </CardsGrid>
            ) : (
              <EmptyState>
                Você ainda não tem poupanças cadastradas.
{' '}
                <Link to="savings-account-register">Clique aqui</Link>
{' '}
para
                criar uma e comece a poupar!
</EmptyState>
            )}
          </Section>
        </Sections>
      </Content>
    </Container>
  );
};

export default AccountsSummaryV2;
